import { useState, useEffect } from 'react';
import { formatFranc } from '../lib/utils';
import api from '../lib/api';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import BackButton from '../components/BackButton';
import {
  BookOpen, ChevronDown, ChevronRight, Zap, X, Globe,
  TrendingDown, Trophy, Users, ArrowRightLeft, Crown, Sparkles,
} from 'lucide-react';
import FrancIcon from '../components/FrancIcon';

/** Render one rule row. Handles flat-value, formula, and sub-cent threshold rules. */
function RuleRow({ rule }) {
  const isFormula = !!rule.formula;
  const label = rule.display_label || rule.label;
  const formulaText = rule.display_formula || rule.formula;
  const displayValue = rule.display_value != null ? rule.display_value : rule.value;
  return (
    <div className="flex items-center justify-between px-4 py-2 border-b border-border/20 last:border-0">
      <span className="text-sm flex items-center gap-2">
        <span className="text-base leading-none" aria-hidden="true">{rule.emoji}</span>
        <span>{label}</span>
      </span>
      {isFormula ? (
        <span className="text-sm font-bold tabular-nums text-[hsl(var(--accent-text))] bg-[hsl(var(--accent)/0.08)] px-2 py-0.5 rounded" data-testid={`formula-${rule.event}`}>
          {formulaText} ₣
        </span>
      ) : (
        <span className="text-sm font-bold tabular-nums text-[hsl(var(--accent-text))]" data-testid={`value-${rule.event}`}>
          {typeof displayValue === 'number' ? formatFranc(displayValue) : `${displayValue} ₣`}
        </span>
      )}
    </div>
  );
}

/** Collapsible category section. */
function CategorySection({ cat, expanded, onToggle }) {
  return (
    <div data-testid={`scoring-cat-${cat.key}`}>
      <button
        className="w-full flex items-center justify-between bg-card border border-border/60 rounded-xl px-4 py-3 hover:bg-secondary/30 transition"
        onClick={() => onToggle(cat.key)}
        data-testid={`scoring-cat-toggle-${cat.key}`}
      >
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">{cat.label}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-[10px]">{(cat.rules || []).length} events</Badge>
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </div>
      </button>
      {expanded && (
        <div className="mt-1 bg-card border border-border/40 rounded-xl overflow-hidden">
          {cat.description && (
            <p className="text-xs text-muted-foreground px-4 py-2 bg-secondary/20 border-b border-border/20">
              {cat.description}
            </p>
          )}
          {(cat.rules || []).map((rule) => <RuleRow key={rule.event} rule={rule} />)}

          {(cat.sub_types || []).map((sub) => (
            <div key={sub.key} className="border-t border-border/40">
              <div className="px-4 py-2 bg-secondary/30">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {sub.label}
                </span>
              </div>
              {(sub.rules || []).map((rule) => (
                <div key={rule.event} className="pl-4"><RuleRow rule={rule} /></div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HowToPlayAndRules({ onBack, isFirstTime = false, onClose }) {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCat, setExpandedCat] = useState('base_entity');  // Universal expanded by default

  useEffect(() => {
    api.get('/scoring/rules/viewer').then(res => {
      setRules(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const toggleCat = (key) => setExpandedCat(expandedCat === key ? null : key);

  return (
    <div className="p-4" data-testid="how-to-play">
      {/* Header */}
      {isFirstTime ? (
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <BookOpen className="h-5 w-5" /> How to Play
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg" data-testid="how-to-play-close">
            <X className="h-5 w-5" />
          </button>
        </div>
      ) : (
        <>
          {onBack && <BackButton onClick={onBack} />}
          <h2 className="text-lg font-semibold tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            <BookOpen className="h-5 w-5 inline mr-2" /> How to Play & Scoring Rules
          </h2>
        </>
      )}

      <ScrollArea className={isFirstTime ? 'h-[calc(100vh-80px)]' : 'h-[calc(100vh-160px)]'}>
        {/* ═══ HOW TO PLAY ═══ */}
        <div className="bg-card border border-border/60 rounded-2xl p-5 mb-6">
          <h3 className="text-base font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <Globe className="h-5 w-5 text-primary" /> How to Play
          </h3>

          <div className="space-y-4 text-sm text-foreground/80 leading-relaxed">
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Users className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground mb-1">Draft Your Team</p>
                <p>Build a team by drafting real people — Rulers, CEOs, Actors, Athletes, Artists, and Influencers. Each league has its own roster requirements and budget. Choose wisely based on who you think will make headlines!</p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-[hsl(var(--accent)/0.15)] flex items-center justify-center shrink-0">
                <FrancIcon className="h-4 w-4 text-[hsl(var(--accent))]" />
              </div>
              <div>
                <p className="font-semibold text-foreground mb-1">Earn Fantasy Dollars (₣)</p>
                <p>Your people earn Fantasy Dollars (₣) two ways. First, a steady trickle from real-world buzz — every wave of social mentions pays out a little ₣, so anyone in the conversation keeps earning. Second, big one-off payouts when notable events actually happen — a wedding, an award, an election win, a record-breaking game. Rarer events pay far more (a Nobel Prize dwarfs a podcast appearance), and every payout follows the transparent Scoring Rules below. <strong>Everything you earn lands straight in your team's cash balance — growing your Net Worth, the score that decides the leaderboard.</strong></p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
                <TrendingDown className="h-4 w-4 text-destructive" />
              </div>
              <div>
                <p className="font-semibold text-foreground mb-1">Living Prices</p>
                <p>Prices are alive — they track each person's overall real-world public-attention volume across major public sources. When someone is making headlines their price climbs; when the buzz fades, their price and form drift back down. Stagnant rosters fall behind, so stay active and make strategic transfers to keep your team competitive. (Your budget is in Fantasy Dollars — in-game points with no cash value that can't be bought with real money.)</p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-green-500/10 flex items-center justify-center shrink-0">
                <ArrowRightLeft className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-foreground mb-1">Transfers & Strategy</p>
                <p>Your first weekly transfer is free. Additional transfers cost a percentage of your budget (2% for the second, 5%+ for more). Set a team captain for a 2x scoring multiplier, and activate Boosters for extra advantages.</p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-yellow-500/10 flex items-center justify-center shrink-0">
                <Trophy className="h-4 w-4 text-yellow-600" />
              </div>
              <div>
                <p className="font-semibold text-foreground mb-1">Compete & Win</p>
                <p>Climb the leaderboards — ranked by <strong>Net Worth</strong> (your cash plus the live value of everyone on your roster) — in the Global Leagues and private leagues with friends. Compound your earnings into a rising portfolio to overtake rivals. Bragging rights, custom league wins, weekly news-driven scoring — pure fantasy, no gambling. The world is your stage!</p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-indigo-500/10 flex items-center justify-center shrink-0">
                <Crown className="h-4 w-4 text-indigo-600" />
              </div>
              <div>
                <p className="font-semibold text-foreground mb-1">League Commissioner</p>
                <p>When you create a private league, you become its Commissioner. You can customize people categories, set roster sizes, manage the starting budget, and kick unwanted players. If you need help with your league, contact us at Gazumba Games.</p>
              </div>
            </div>
          </div>
        </div>

        {/* ═══ SCORING RULES ═══ */}
        <h3 className="text-base font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
          <Zap className="h-5 w-5 text-[hsl(var(--accent))]" /> Scoring Rules
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          All scored news events generate positive Fantasy Dollars (₣) only. Category labels like <i>Athlete</i>, <i>Artist</i>, or <i>CEO</i> mark which type of person the scoring rule applies to — some rules are universal and apply to everyone. The person you choose as Captain gets a 2× multiplier on every ₣ they earn. Boosters stack on top of that: you get one of each (Stage—whole roster—Boost, Wildcard, Captain 3×, and one Booster per people category) — once activated, the Booster multiplier is active for the entire week, but you can only use one Booster per week. Once a Booster is used up, it's gone for the duration of your league.
        </p>

        {loading ? (
          <div className="space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : !rules ? (
          <p className="text-center text-muted-foreground">Failed to load scoring rules</p>
        ) : (
          <div className="space-y-2 mb-6">
            {/* ── Universal "All People" section — same chrome as the
                category rows now (v5.7.48), so it doesn't look pinned. ── */}
            {rules.universal && (rules.universal.rules || []).length > 0 && (
              <div data-testid="scoring-cat-universal">
                <button
                  className="w-full flex items-center justify-between bg-card border border-border/60 rounded-xl px-4 py-3 hover:bg-secondary/30 transition"
                  onClick={() => toggleCat('base_entity')}
                  data-testid="scoring-cat-toggle-universal"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-semibold">{rules.universal.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-[10px]">
                      {(rules.universal.rules || []).length} events
                    </Badge>
                    {expandedCat === 'base_entity' ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </div>
                </button>
                {expandedCat === 'base_entity' && (
                  <div className="mt-1 bg-card border border-border/40 rounded-xl overflow-hidden">
                    {rules.universal.description && (
                      <p className="text-xs text-muted-foreground px-4 py-2 bg-secondary/20 border-b border-border/20">
                        {rules.universal.description}
                      </p>
                    )}
                    {(rules.universal.rules || []).map((rule) => <RuleRow key={rule.event} rule={rule} />)}
                  </div>
                )}
              </div>
            )}

            {/* ── Category-specific sections ── */}
            {(rules.categories || []).map((cat) => (
              <CategorySection
                key={cat.key}
                cat={cat}
                expanded={expandedCat === cat.key}
                onToggle={toggleCat}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
